"use client"

import { FormikProvider, type FormikErrors, useFormik } from "formik"

import { FormInput } from "@/components/form-inputs"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useReconcileDeliveryShipmentMutation } from "@/hooks/mutations/delivery-shipments"
import type { DeliveryShipmentListItem } from "@/server-actions/delivery-shipments"
import { useTranslations } from "next-intl"


type ReconciliationFormValues = {
  providerShipmentId: string
  trackingNumber: string
}

const initialValues: ReconciliationFormValues = {
  providerShipmentId: "",
  trackingNumber: "",
}

export function ReconcileDeliveryShipmentDialog({
  shipment,
  onOpenChange,
}: {
  shipment: DeliveryShipmentListItem | null
  onOpenChange: (open: boolean) => void
}) {
  const tUi = useTranslations("Deliveries.DeliveriesReconcileDeliveryShipmentDialog")
  const tCommonUi = useTranslations("Common")

  const mutation = useReconcileDeliveryShipmentMutation()
  const formik = useFormik<ReconciliationFormValues>({
    initialValues,
    enableReinitialize: true,
    validate: values => {
      const errors: FormikErrors<ReconciliationFormValues> = {}
      if (!values.providerShipmentId.trim()) {
        errors.providerShipmentId = tUi("novaPostShipmentIDIsRequired")
      }
      if (!values.trackingNumber.trim()) {
        errors.trackingNumber = tUi("trackingNumberIsRequired")
      }
      return errors
    },
    onSubmit: async (values, helpers) => {
      if (!shipment) return
      try {
        await mutation.mutateAsync({
          shipmentId: shipment.id,
          providerShipmentId: values.providerShipmentId.trim(),
          trackingNumber: values.trackingNumber.trim(),
          setErrors: errors => helpers.setErrors(
            errors as FormikErrors<ReconciliationFormValues>,
          ),
        })
        helpers.resetForm()
        onOpenChange(false)
      } catch {
        // withQueryHandling has already surfaced the safe server error.
      }
    },
  })

  return (
    <Dialog open={shipment !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle><span key={tUi("verifyDeliveryInNovaPost")}>{tUi("verifyDeliveryInNovaPost")}</span></DialogTitle>
          <DialogDescription>
            <span key={tUi("versafyDidNotReceiveAConclusiveCreationResponse")}>{tUi("versafyDidNotReceiveAConclusiveCreationResponse")}</span></DialogDescription>
        </DialogHeader>
        <Alert>
          <AlertTitle><span key={tUi("thisDoesNotCreateOrRetryAWaybill")}>{tUi("thisDoesNotCreateOrRetryAWaybill")}</span></AlertTitle>
          <AlertDescription>
            <span key={tUi("confirmTheOrderReferenceProviderShipmentIDAnd")}>{tUi("confirmTheOrderReferenceProviderShipmentIDAnd")}</span></AlertDescription>
        </Alert>
        {shipment ? (
          <p className="text-sm text-muted-foreground">
            <span><span key={tUi("orderReference")}>{tUi("orderReference")}</span></span> <span className="font-medium text-foreground">
              {shipment.orderReference}
            </span>
          </p>
        ) : null}
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            <FormInput
              name="providerShipmentId"
              label={tUi("novaPostShipmentID")}
              autoComplete="off"
              maxLength={500}
              required
            />
            <FormInput
              name="trackingNumber"
              label={tUi("trackingNumber")}
              autoComplete="off"
              maxLength={500}
              required
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={formik.isSubmitting}
              >
                <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
              <Button type="submit" disabled={formik.isSubmitting || !shipment}>
                <span key={formik.isSubmitting ? tUi("recording") : tUi("recordVerifiedWaybill")}>{formik.isSubmitting ? tUi("recording") : tUi("recordVerifiedWaybill")}</span>
              </Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}
